import { useState } from 'react'
import { useApi } from '../../../hooks/useApi'
import { RelocateApiService } from '../services/relocateApi'
import { ValidatePositionResponse } from '../types'
import { checkIfCommentsRequired } from '../utils/relocateUtils'

interface UseDestinyValidationProps {
  sharedState: {
    sku: string
    productOriginId: number | null
    originPositionType?: number
    posicionOrigen: string
    posicionDestino?: string
    cantidad: number | null
    stockKeepingUnitId?: number
    selectedBatch?: { batchId: number }
  }
  setSharedState: (updater: (prev: any) => any) => void
  onValidationSuccess: () => void
  relocateCommentaryEnabled: boolean
}

/**
 * Reglas duras de la posición destino (MaxCapacity/MonoSku/MonoBatch): a diferencia de
 * otras validaciones del flujo, estas no admiten "confirmar de todas formas" — se
 * bloquean directo, porque el backend las rechazaría igual al guardar. Se evalúan acá
 * (apenas se valida el destino) en vez de esperar al Confirmar final, ya que es el punto
 * más temprano del flujo donde se conocen tanto la cantidad como el destino.
 */
const getDestinationAttributeError = (
  destination: ValidatePositionResponse,
  quantity: number,
  skuId?: number,
  batchId?: number
): string | null => {
  const attr = destination.positionAttribute
  if (!attr) return null

  if (attr.monoSku && destination.currentSkuId != null && destination.currentSkuId !== skuId) {
    return destination.currentSku
      ? `Ubicación monosku. Solo permite el SKU: ${destination.currentSku}.`
      : 'Ubicación monosku. Solo permite un SKU en esta posición.'
  }

  if (attr.monoBatch && destination.currentBatchId != null && destination.currentBatchId !== batchId) {
    return destination.currentBatchNumber
      ? `Ubicación monolote. Solo permite el lote: ${destination.currentBatchNumber}.`
      : 'Ubicación monolote. Solo permite un lote en esta posición.'
  }

  if (attr.maxCapacity > 0) {
    const total = (destination.currentStockQty ?? 0) + quantity
    if (total > attr.maxCapacity) {
      return `Capacidad máxima excedida. Ubicación con capacidad máxima configurada: ${attr.maxCapacity} unidades, ya está completa.`
    }
  }

  return null
}

interface UseDestinyValidationReturn {
  localDestinyPosition: string
  setLocalDestinyPosition: (value: string) => void
  isValidating: boolean
  destinyValidated: boolean
  setDestinyValidated: (value: boolean) => void
  destinyValidationFailed: boolean
  error: string
  setError: (value: string) => void
  handleValidateDestiny: (scannedValue?: string) => Promise<void>
  handleEditDestiny: () => void
}

export const useDestinyValidation = ({
  sharedState,
  setSharedState,
  onValidationSuccess,
  relocateCommentaryEnabled
}: UseDestinyValidationProps): UseDestinyValidationReturn => {
  // Inicializar con valores existentes de sharedState (para cuando se vuelve desde ConfirmationStep)
  const [localDestinyPosition, setDestinyPositionValue] = useState(sharedState.posicionDestino || '')
  const [isValidating, setIsValidating] = useState(false)
  const [destinyValidated, setDestinyValidated] = useState(Boolean(sharedState.posicionDestino))
  const [destinyValidationFailed, setDestinyValidationFailed] = useState(false)
  const [error, setError] = useState('')

  const setLocalDestinyPosition = (value: string) => {
    setDestinyPositionValue(value)
    setError('')
    setDestinyValidationFailed(false)
  }

  const { callApi: validatePositionCallApi } = useApi<ValidatePositionResponse>()

  /**
   * Acepta el valor por parametro para el escaneo por camara: setLocalDestinyPosition
   * es asincrono, asi que leer del estado en el mismo tick usaria el valor anterior.
   * Sin argumento se comporta igual que antes (Enter y boton Validar).
   */
  const handleValidateDestiny = async (scannedValue?: string) => {
    const destinyValue = (scannedValue ?? localDestinyPosition).trim()
    if (!destinyValue) {
      setError('Ingrese una posición de destino válida')
      return
    }

    if (destinyValue.toLowerCase() === sharedState.posicionOrigen.trim().toLowerCase()) {
      setError('La posición de destino debe ser diferente a la de origen')
      return
    }

    setIsValidating(true)
    setError('')
    setDestinyValidationFailed(false)

    try {
      const response = await validatePositionCallApi(RelocateApiService.validateDestinyPosition(
        destinyValue,
        sharedState.sku,
        sharedState.productOriginId!
      ))

      if (response) {
        const attributeError = getDestinationAttributeError(
          response,
          sharedState.cantidad ?? 0,
          sharedState.stockKeepingUnitId,
          sharedState.selectedBatch?.batchId
        )
        if (attributeError) {
          setError(attributeError)
          setDestinyValidated(false)
          setDestinyValidationFailed(true)
          return
        }

        const destinyTypeId = response.typePositionId
        const originTypeId = sharedState.originPositionType
        const requiresComments = relocateCommentaryEnabled && checkIfCommentsRequired(originTypeId, destinyTypeId)

        setSharedState(prev => ({
          ...prev,
          posicionDestino: destinyValue,
          destinyPositionId: response.positionId,
          destinyPositionType: destinyTypeId,
          requiresComments: Boolean(requiresComments)
        }))

        setDestinyValidated(true)

        if (requiresComments) {
          onValidationSuccess()
        }
      }
    } catch (err) {
      // callApi ya rechaza con el mensaje real del backend (string), formateado por
      // generateErrorFromResponse — no con el error de axios crudo.
      setError(typeof err === 'string' ? err : 'Posición de destino no válida')
      setDestinyValidated(false)
      setDestinyValidationFailed(true)
    } finally {
      setIsValidating(false)
    }
  }

  const handleEditDestiny = () => {
    setDestinyValidated(false)
    setDestinyValidationFailed(false)
    setLocalDestinyPosition('')
    setSharedState(prev => ({
      ...prev,
      posicionDestino: '',
      destinyPositionId: undefined,
      destinyPositionType: undefined
    }))
    setError('')
  }

  return {
    localDestinyPosition,
    setLocalDestinyPosition,
    isValidating,
    destinyValidated,
    setDestinyValidated,
    destinyValidationFailed,
    error,
    setError,
    handleValidateDestiny,
    handleEditDestiny
  }
}